import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
  HttpResponse,
} from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, Observable, throwError } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class HttpCrudService {
  private readonly http = inject(HttpClient); //injecting HttpClient
  private readonly headers = new HttpHeaders().set(
    'content-type',
    'application/json',
  );
  constructor() {}

  /**
   * Sends an HTTP GET request to the specified endpoint and returns an observable of the response.
   *
   * @typeParam T - The expected response type.
   * @param endpoint - The URL or endpoint to which the GET request is sent.
   * @returns An `Observable` emitting the response of type `T`.
   * @throws Emits an error if the HTTP request fails, handled by the `errorHandler`.
   */
  get<T>(endpoint: string): Observable<T> {
    return this.http.get<T>(`${endpoint}`).pipe(catchError(this.errorHandler));
  }

  /**
   * Retrieves file data from the specified endpoint as a Blob wrapped in an HttpResponse.
   *
   * @template T The expected response type (not used directly, but allows for type inference).
   * @param endpoint The URL endpoint from which to fetch the file data.
   * @returns An Observable that emits an HttpResponse containing the file data as a Blob.
   * @throws Emits an error if the HTTP request fails.
   */
  getFileData<T>(endpoint: string): Observable<HttpResponse<Blob>> {
    return this.http
      .get(`${endpoint}`, { observe: 'response', responseType: 'blob' })
      .pipe(catchError(this.errorHandler));
  }

  /**
   * Sends a POST request to the specified endpoint with the provided payload.
   *
   * @typeParam T - The expected response type.
   * @param endpoint - The URL or endpoint to which the POST request is sent.
   * @param payload - The data to be sent in the body of the POST request.
   * @returns An Observable emitting the response of type T.
   */
  post<T>(endpoint: string, payload: any): Observable<T> {
    return this.http
      .post<T>(`${endpoint}`, payload, { headers: this.headers })
      .pipe(catchError(this.errorHandler));
  }

  postFileData(endpoint: string, payload: any): Observable<HttpResponse<Blob>> {
    return this.http
      .post(`${endpoint}`, payload, {
        headers: this.headers,
        observe: 'response',
        responseType: 'blob',
      })
      .pipe(catchError(this.errorHandler));
  }

  /**
   * Sends a POST request to the specified endpoint with a FormData payload.
   *
   * @typeParam T - The expected response type.
   * @param endpoint - The URL or endpoint to which the file should be posted.
   * @param payload - The FormData object containing the file and any additional data to be sent.
   * @returns An Observable emitting the response of type T.
   */
  postFile<T>(endpoint: string, payload: FormData): Observable<T> {
    return this.http
      .post<T>(`${endpoint}`, payload)
      .pipe(catchError(this.errorHandler));
  }

  /**
   * Sends an HTTP DELETE request to the specified endpoint.
   *
   * @template T The expected type of the response.
   * @param {string} endpoint - The API endpoint to which the DELETE request is sent.
   * @returns {Observable<T>} An observable containing the response of type T.
   *
   * This method also handles errors using a centralized error handler.
   */

  delete<T>(endpoint: string): Observable<T> {
    return this.http
      .delete<T>(`${endpoint}`)
      .pipe(catchError(this.errorHandler));
  }

  /**
   * Handles HTTP errors by logging the error message to the console and returning an observable that emits an error.
   *
   * @param error - The HttpErrorResponse object containing error details from an HTTP request.
   * @returns An observable that emits an Error with the error message or a default 'Server Error' message.
   */
  errorHandler = (error: HttpErrorResponse) => {
    console.error(error.message);
    return throwError({
      message: error.message || 'Server Error',
      status: error.status,
      error: error.error,
    });
  };
}
